import { MAX_INPUT_VALUE } from '@/editor/constant/input';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { worldPosToClientPos } from '@/editor/utils';
import { isSafari } from '@/utils/browser/platform';
import { debounce } from 'lodash-es';

const preventCaretReset = (e: MouseEvent) => e.preventDefault();

export interface ITextModelInfo {
  id: string;
  position: IPoint2d;
  value: string;
  editable?: boolean;
  trapFocus?: boolean;
  blurOnCommit?: boolean;
  max?: number;
}

interface ITextModelOptions {
  onChange?: (id: string, value: number) => void;
  onCommit?: (id: string, value: number) => void;
  onFocus?: (id?: string) => void;
}

export class TextModel {
  private _container: HTMLDivElement;
  private _disposer: () => void = () => {};
  private _onChange: null | ITextModelOptions['onChange'];
  private _onCommit: null | ITextModelOptions['onCommit'];
  private _onFocus: null | ITextModelOptions['onFocus'];
  private _rafId: number | null = null;

  constructor(options: ITextModelOptions) {
    this._onChange = options.onChange || null;
    this._onCommit = options.onCommit || null;
    this._onFocus = options.onFocus || null;

    this._container = document.createElement('div');
    globalStores.editorStateStore.rulerContainer.appendChild(this._container);
    const disposeViewChange = editorEvent.on(EditorEventName.VIEW2D_CHANGE, this._handleVieeChange);
    const disposeResize = editorEvent.on(EditorEventName.RESIZE, this._handleVieeChange);
    this._disposer = () => {
      disposeViewChange();
      disposeResize();
    };
  }

  private _debounceUpdatePosition = debounce(() => this.updatePosition(), 300);

  private _handleVieeChange = () => {
    this._container.style.display = 'none';
    this._debounceUpdatePosition();
  };

  private _handleInput = (e: InputEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    const inputEl = e.target;

    const originalValue = inputEl.value;

    // 记录当前光标位置
    const prevCursor = inputEl.selectionStart ?? 0;

    // 清除“数字”和“.”以外的字符
    let cleanedValue: string = originalValue.replace(/[^\d.]/g, '');

    // 确保不能以小数点开头
    cleanedValue = cleanedValue.replace(/^\./g, '');

    // 只保留第一个小数点，清除多余的小数点
    cleanedValue = cleanedValue.replace('.', '$#$').replace(/\./g, '').replace('$#$', '.');

    let isCapped = false;
    const parsedValue = parseFloat(cleanedValue);
    const maxValueString = inputEl.dataset.max;
    const MAX_VALUE =
      maxValueString && !Number.isNaN(parseInt(maxValueString, 10)) ? parseInt(maxValueString, 10) : MAX_INPUT_VALUE;

    if (!isNaN(parsedValue) && parsedValue > MAX_VALUE) {
      cleanedValue = MAX_VALUE.toString();
      isCapped = true;
    }

    if (originalValue !== cleanedValue) {
      inputEl.value = cleanedValue;

      if (isCapped) {
        // 如果触发了最大值截断，直接将光标定位到最后，避免光标错乱
        inputEl.setSelectionRange(cleanedValue.length, cleanedValue.length);
      } else {
        // 正常的非数字过滤，精密计算并恢复光标位置
        const nonAllowedBeforeCursor: number = (originalValue.slice(0, prevCursor).match(/[^\d.]/g) || []).length;
        const newCursor: number = prevCursor - nonAllowedBeforeCursor;
        inputEl.setSelectionRange(newCursor, newCursor);
      }
    }
  };

  private _handleMouseDown = (e: MouseEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    // Safari 直到 mouseup 才按点击位置设置插入符,会把 focus 阶段的全选塌缩成光标
    // 已聚焦时不拦截,保留点击定位光标的能力
    if (!isSafari() || document.activeElement === e.target) return;

    document.addEventListener('mouseup', preventCaretReset, { once: true });
  };

  private _handleFocus = (e: FocusEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    // 聚焦时自动全选
    e.target.select();
    this._onFocus?.(e.target.dataset.id);
  };

  private _handleKeydown = (e: KeyboardEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const inputEl = e.target;
    const prevCursor = inputEl.selectionStart;
    const parsedValue = parseFloat(inputEl.value);
    const value = isNaN(parsedValue) ? 0 : parsedValue;
    const roundValue = Math.round(value);

    switch (e.key) {
      case 'Tab': {
        if (this._getShouldFocusTrap()) {
          e.preventDefault();
          this._focusTrap();
        }
        if (this._onChange && !isNaN(parsedValue)) {
          inputEl.value = roundValue.toString();
          const id = inputEl.getAttribute('data-id') || '';
          this._onChange(id, roundValue);
        }
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (this._onCommit && !isNaN(parsedValue)) {
          inputEl.value = roundValue.toString();
          const id = inputEl.getAttribute('data-id') || '';
          const blurOnCommit = inputEl.getAttribute('data-bluroncommit') === 'true';
          if (blurOnCommit) {
            // 提交会触发标尺重建,先清除聚焦记录,避免重建后的输入框按残留 focusId 自动聚焦
            globalStores.editorStateStore.resetTextModelFocusData();
            this._onCommit(id, roundValue);
            inputEl.blur();
          } else {
            this._onCommit(id, roundValue);
          }
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const newValue = value + 1;
        inputEl.value = newValue.toString();
        if (prevCursor) {
          inputEl.setSelectionRange(prevCursor, prevCursor);
        }
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        const newValue = value - 1;
        inputEl.value = newValue.toString();
        if (prevCursor) {
          inputEl.setSelectionRange(prevCursor, prevCursor);
        }
        break;
      }
    }
  };

  createModel(textInfos: ITextModelInfo[], focusId?: string) {
    textInfos.forEach((textInfo) => {
      const { position, value } = textInfo;

      let element: HTMLElement;

      if (textInfo.editable) {
        const inputElement = document.createElement('input');
        inputElement.type = 'text';
        inputElement.autocomplete = 'off';
        inputElement.inputMode = 'numeric';
        inputElement.pattern = '[0-9]+([.][0-9]+)?';
        inputElement.classList.add('ruler-input');
        inputElement.value = value;
        inputElement.dataset.id = textInfo.id;
        inputElement.dataset.max = (textInfo.max ?? MAX_INPUT_VALUE).toString();
        inputElement.dataset.bluroncommit = textInfo.blurOnCommit ? 'true' : 'false';
        element = inputElement;

        if (textInfo.trapFocus) {
          inputElement.setAttribute('data-focus-trap-enabled', 'true');
        }

        if (focusId && textInfo.id === focusId) {
          requestAnimationFrame(() => {
            inputElement.focus();
          });
        }

        // focus 自动全选
        inputElement.addEventListener('mousedown', this._handleMouseDown);
        inputElement.addEventListener('focus', this._handleFocus);
        inputElement.addEventListener('input', this._handleInput);
        inputElement.addEventListener('keydown', this._handleKeydown);
      } else {
        const textEl = document.createElement('span');
        textEl.classList.add('ruler-text');
        textEl.innerText = value;
        element = textEl;
      }

      element.dataset.x = `${position.x}`;
      element.dataset.y = `${position.y}`;

      this._container.appendChild(element);
    });

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this.updatePosition();
    });
  }

  updatePosition() {
    const {
      renderer,
      scene2d: { camera }
    } = globalStores.editorStateStore.sceneInfo;
    for (let i = 0; i < this._container.children.length; i++) {
      const element = this._container.children[i] as HTMLElement;
      const x = parseFloat(element.dataset.x || '0');
      const y = parseFloat(element.dataset.y || '0');
      const canvasPosition = worldPosToClientPos({ x, y }, camera, renderer);
      element.style.transform = `translate(${canvasPosition.x}px, ${canvasPosition.y}px) translate(-50%, -50%)`;
    }

    this._container.style.display = 'block';
  }

  private _getShouldFocusTrap() {
    const focusableElements = this._container.querySelectorAll('input[data-focus-trap-enabled="true"]');
    return focusableElements.length > 0;
  }

  private _focusTrap() {
    const focusableElements = this._container.querySelectorAll<HTMLInputElement>(
      'input[data-focus-trap-enabled="true"]'
    );
    if (focusableElements.length) {
      const currentIndex = Array.from(focusableElements).findIndex((el) => el === document.activeElement);
      const nextIndex = (currentIndex + 1) % focusableElements.length;

      focusableElements[nextIndex].focus();
      if (this._onFocus) {
        this._onFocus(focusableElements[nextIndex].dataset.id);
      }
    }
  }

  clear() {
    this._container.replaceChildren();
  }

  dispose() {
    const { rulerContainer } = globalStores.editorStateStore;
    this._disposer();
    try {
      if (rulerContainer && rulerContainer.contains(this._container)) {
        rulerContainer.removeChild(this._container);
      }
    } catch (error) {
      console.error({
        error,
        rulerContainer,
        container: this._container
      });
    }
    this._container.replaceChildren();
  }
}
